<template>
  <div class="ai-admin-page">
    <header class="ai-page-header">
      <div>
        <h1>商城知识库</h1>
        <p>
          维护商城制度、售后流程和标准话术。AI
          只检索已启用的内容，价格、库存和订单状态仍通过实时工具查询。
        </p>
      </div>
      <el-button type="primary" icon="el-icon-plus" @click="openEditor()">新增知识</el-button>
    </header>

    <section class="ai-surface">
      <div class="ai-toolbar">
        <div class="ai-toolbar__filters">
          <el-input
            v-model.trim="filters.keyword"
            clearable
            prefix-icon="el-icon-search"
            placeholder="搜索标题或正文"
            style="width: 260px"
            @keyup.enter.native="loadRows(1)"
          />
          <el-select
            v-model="filters.category"
            clearable
            placeholder="全部分类"
            style="width: 150px"
            @change="loadRows(1)"
          >
            <el-option
              v-for="item in categories"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
          <el-select
            v-model="filters.status"
            clearable
            placeholder="全部状态"
            style="width: 130px"
            @change="loadRows(1)"
          >
            <el-option label="已启用" :value="1" />
            <el-option label="已停用" :value="0" />
          </el-select>
          <el-button icon="el-icon-search" @click="loadRows(1)">查询知识</el-button>
        </div>
        <span class="ai-status-line"
          ><span class="ai-status-dot is-ready" />共 {{ total }} 条知识</span
        >
      </div>

      <el-table v-loading="loading" :data="rows" empty-text="暂无知识内容" style="width: 100%">
        <el-table-column label="知识标题" min-width="220">
          <template slot-scope="scope">
            <strong class="knowledge-title">{{ scope.row.title }}</strong>
            <p class="knowledge-preview">{{ scope.row.content }}</p>
          </template>
        </el-table-column>
        <el-table-column label="分类" width="120">
          <template slot-scope="scope">{{ categoryLabel(scope.row.category) }}</template>
        </el-table-column>
        <el-table-column label="来源" width="100">
          <template>人工维护</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag size="small" :type="scope.row.status === 1 ? 'success' : 'info'">{{
              scope.row.status === 1 ? "已启用" : "已停用"
            }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" width="180">
          <template slot-scope="scope">{{ formatTime(scope.row.mddate) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="openEditor(scope.row)">编辑</el-button>
            <el-button type="text" class="danger-link" @click="removeRow(scope.row)"
              >删除</el-button
            >
          </template>
        </el-table-column>
      </el-table>

      <div class="knowledge-pagination">
        <el-pagination
          background
          layout="total, prev, pager, next"
          :current-page="filters.page"
          :page-size="filters.pageSize"
          :total="total"
          @current-change="loadRows"
        />
      </div>
    </section>

    <el-drawer
      :title="form.id ? '编辑知识' : '新增知识'"
      :visible.sync="drawerVisible"
      size="520px"
      :wrapper-closable="!saving"
    >
      <div class="knowledge-editor">
        <el-form ref="form" :model="form" :rules="rules" label-position="top">
          <el-form-item label="知识标题" prop="title">
            <el-input
              v-model.trim="form.title"
              maxlength="120"
              show-word-limit
              placeholder="例如：七天无理由退货规则"
            />
          </el-form-item>
          <el-form-item label="知识分类" prop="category">
            <el-select v-model="form.category" style="width: 100%">
              <el-option
                v-for="item in categories"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="知识正文" prop="content">
            <el-input
              v-model.trim="form.content"
              type="textarea"
              :rows="14"
              maxlength="12000"
              show-word-limit
              placeholder="写清适用条件、处理步骤和例外情况，避免只写一句结论。"
            />
          </el-form-item>
          <el-form-item label="启用状态">
            <el-switch
              v-model="form.status"
              :active-value="1"
              :inactive-value="0"
              active-text="允许 AI 检索"
            />
          </el-form-item>
        </el-form>
        <div class="ai-note">
          <i class="el-icon-info" /><span
            >保存后立即进入本地知识库。后续接入百炼向量库时，将按条目更新时间增量同步。</span
          >
        </div>
      </div>
      <div class="drawer-actions">
        <el-button @click="drawerVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submit">保存知识</el-button>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { deleteKefuKnowledge, getKefuKnowledgeList, saveKefuKnowledge } from "@/api/kefu";

const emptyForm = () => ({ id: null, title: "", category: "GENERAL", content: "", status: 1 });

export default {
  name: "KefuKnowledge",
  data() {
    return {
      loading: false,
      saving: false,
      drawerVisible: false,
      rows: [],
      total: 0,
      filters: { keyword: "", category: "", status: null, page: 1, pageSize: 20 },
      form: emptyForm(),
      categories: [
        { label: "通用说明", value: "GENERAL" },
        { label: "订单支付", value: "ORDER" },
        { label: "物流配送", value: "LOGISTICS" },
        { label: "退换售后", value: "AFTERSALE" },
        { label: "会员积分", value: "MEMBER" },
        { label: "活动规则", value: "CAMPAIGN" },
        { label: "客服话术", value: "SCRIPT" }
      ],
      rules: {
        title: [{ required: true, message: "请输入知识标题", trigger: "blur" }],
        category: [{ required: true, message: "请选择知识分类", trigger: "change" }],
        content: [{ required: true, message: "请输入知识正文", trigger: "blur" }]
      }
    };
  },
  created() {
    this.loadRows(1);
  },
  methods: {
    async loadRows(page = this.filters.page) {
      this.filters.page = page;
      this.loading = true;
      try {
        const response = await getKefuKnowledgeList(this.filters);
        const data = (response && response.data) || {};
        this.rows = data.entitys || [];
        this.total = data.total || 0;
      } finally {
        this.loading = false;
      }
    },
    openEditor(row) {
      this.form = row
        ? {
            id: row.id,
            title: row.title,
            category: row.category,
            content: row.content,
            status: row.status
          }
        : emptyForm();
      this.drawerVisible = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate());
    },
    submit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return;
        this.saving = true;
        try {
          await saveKefuKnowledge(this.form);
          this.$message.success("知识已保存");
          this.drawerVisible = false;
          await this.loadRows(this.form.id ? this.filters.page : 1);
        } finally {
          this.saving = false;
        }
      });
    },
    async removeRow(row) {
      await this.$confirm(`确认删除“${row.title}”？`, "删除知识", { type: "warning" });
      await deleteKefuKnowledge(row.id);
      this.$message.success("知识已删除");
      await this.loadRows(this.filters.page);
    },
    categoryLabel(value) {
      const item = this.categories.find(category => category.value === value);
      return item ? item.label : value || "通用说明";
    },
    formatTime(value) {
      return value ? new Date(value).toLocaleString() : "-";
    }
  }
};
</script>

<style lang="scss">
@import "./ai-management.scss";
.knowledge-title {
  display: block;
  margin-bottom: 5px;
  color: #202332;
}
.knowledge-preview {
  max-width: 62ch;
  margin: 0;
  overflow: hidden;
  color: #6a7082;
  font-size: 13px;
  line-height: 1.5;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.knowledge-pagination {
  display: flex;
  justify-content: flex-end;
  padding: 16px 18px;
  border-top: 1px solid #eceef4;
}
.knowledge-editor {
  padding: 4px 24px 84px;
}
.drawer-actions {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid #eceef4;
  background: #fff;
}
.danger-link {
  color: #d44b4b !important;
}
</style>
